import { useContext, type JSX } from "react";
import type { EntityRendererProps } from "../../runtime/contracts/agent";
import { BaseEntityCard } from "../_base/BaseEntityCard";
import { ActionPrefix } from "../_base/ActionPrefix";
import { ExpansionContext } from "../../components/agent/expansionContext";
import { useEpisodeSummary } from "./hooks/useEpisodeSummary";

/**
 * SINGLE canonical episode card. Per `docs/frontend/module-standard.md`
 * ("ONE COMPONENT PER ENTITY"): reads `expanded` from `ExpansionContext`
 * and switches between compact (just the name) and expanded (name +
 * lazily-fetched summary text). The summary hook only fetches on
 * demand; nothing fires until `expanded === true` and an id is present.
 */

/**
 * Chevron gating for episodes. We can't know synchronously whether a
 * summary exists without hitting the RPC, so we require a non-empty
 * entity id and let the expanded view render nothing when the summary
 * resolves to null. The dead-click cost is acceptable.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function episodeHasMore(data: Readonly<Record<string, unknown>>): boolean {
  return typeof data.id === "string" && data.id.length > 0;
}

export function EpisodeCard(props: EntityRendererProps): JSX.Element {
  const { data, action } = props;
  const name =
    (data.name as string | undefined) ??
    (data.title as string | undefined) ??
    "Episode";
  const { expanded } = useContext(ExpansionContext);
  const entityId = expanded ? (data.id as string | undefined) : undefined;
  const { summary, isLoading } = useEpisodeSummary(entityId);

  return (
    <BaseEntityCard {...props}>
      <div className="min-w-0 flex-1">
        <span className="block truncate text-[12px] font-medium text-content">
          <ActionPrefix action={action} />
          {name}
        </span>
        {expanded && !isLoading && summary && (
          <div className="mt-1 whitespace-pre-wrap break-words text-[11px] text-content">
            {summary}
          </div>
        )}
      </div>
    </BaseEntityCard>
  );
}
